/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';

import { Article } from '@models/article.model';
import { User } from '@models/user.model';
import { CapacitorService } from '@app/services/common/capacitor.service';
import { ConfigService } from '@services/common/config.service';
import { UtilsService } from '@services/utils.service';
import { SlugifyPipe } from '@app/pipes/slugify.pipe';
import { StorageService } from './common/storage.service';
import { Config } from '@app/models/config.model';
import { SettingsService } from './settings.service';
import { Settings } from '@app/models/settings.model';
import { pianoAnalytics } from 'piano-analytics-js';
import { Gare } from '@app/models/gare.model';
import { Ligne } from '@app/models/ligne.model';


@Injectable()
export class PianoAnalyticsService {
    private readonly slugifyPipe: SlugifyPipe = new SlugifyPipe();
    private readonly config: Config;
    private user: User;
    private readonly pa = pianoAnalytics
    constructor(
        private readonly capacitorService: CapacitorService,
        private readonly settingsService: SettingsService,
        private readonly utilsService: UtilsService,
        private readonly storageService: StorageService,
        private readonly configService: ConfigService
    ) {
        this.config = configService.getConfig();
        this.settingsService.getSettings().subscribe(async (settings: Settings) => {
            this.user = settings.user;
        })
        this.pa.setConfigurations({ // Basic configuration to send events
            site: this.config.PIANO_ANALYTICS.ID,
            collectDomain:'https://nssvsmp.pa-cd.com',
            log: 'debug'
        });

    }

    public init(): void {
        try {
            console.log('[Analytics][Init] ', this.config.PIANO_ANALYTICS.ID);
            
            const version = this.utilsService.getAppVersion();
            this.pa.setProperty('appVersion', version);
            
            this.trackPropertiesPageDisplay();
            
            console.log('[Analytics][EndInit] ', this.config.PIANO_ANALYTICS.ID);
        } catch (e) {
            console.error('An error occured while initializing Piano Analytics');
        }
    }

    public initCapacitor(): void {
        try {
            console.log('[Analytics][Init] ', this.config.PIANO_ANALYTICS.ID);

            const version = this.utilsService.getAppVersion();
            this.pa.setProperty('appVersion', version);
            
            this.pa.setUser(this.user?.id, 'user', true);

            this.trackPropertiesPageDisplay();

            console.log('[Analytics][EndInit] ', this.config.PIANO_ANALYTICS.ID);
        } catch (error) {
            console.error('An error occured while initializing Piano Analytics');

        }
    }

    async cannotSendAnalytics(): Promise<boolean> {
        return await this.storageService.getAnalyticsStatus();
    }

    public async trackPageView(pageTitle: string, pagePath: string): Promise<void> {
        if (await this.cannotSendAnalytics()) {
            console.log('[DEACTIVATED][Analytics][trackPageView] ' + JSON.stringify(pageTitle));
            return;
        }
        let lignesLibelle: string[] = await this.settingsService.getSubscribedLigneLibelle();
        const version = this.utilsService.getAppVersion();
        

        console.log('[Analytics][trackPageView] ', pageTitle, pagePath);
        this.pa.sendEvent('page.display',
            {
                page: pageTitle,
                id_peg_sam: this.user?.id,
                etablissement: this.user?.etablissementLibelle,
                poste: this.user?.posteLibelle ,
                secteur: this.user?.secteurLibelle ,
                transilien: this.settingsService.isTransilient(),
                LigneLibelle: lignesLibelle || null,
                appVersion: version
            },
        );


    }

    public async trackEvent(eventAction: string, data?: {[key: string]: any}): Promise<void> {
        if (await this.cannotSendAnalytics()) {
            console.log('[DEACTIVATED][Analytics][trackEvent] ' + JSON.stringify(eventAction));
            return;
        }
        const version = this.utilsService.getAppVersion();
        let lignesLibelle: string[] = await this.settingsService.getSubscribedLigneLibelle();

        data.id_peg_sam = this.user?.id;
        data.etablissement = this.user?.etablissementLibelle;
        data.poste = this.user?.posteLibelle;
        data.secteur = this.user?.secteurLibelle;
        data.transilien= this.settingsService.isTransilient();
        data.LigneLibelle = lignesLibelle;
        data.appVersion = version;

        console.log('[Analytics][trackEvent] ', eventAction);
        this.pa.sendEvent(eventAction,
            data
        );
    }

    public async trackEventWithProperties(eventProperties: { [key: string]: any }): Promise<void> {
        if (await this.cannotSendAnalytics()) {
            console.log('[DEACTIVATED][Analytics][trackEvent]');
            return;
        }

        const finalEventCategory = eventProperties.event_category || 'click.action';
        console.log('[Analytics][trackEvent]', eventProperties);
        eventProperties.id_peg_sam = this.user?.id;
        this.pa.sendEvent(finalEventCategory ,
            eventProperties,
        );
        
    }

    public initUser(): void {

        const mymemocUserId = localStorage.getItem('mymemoc_id_user');

        const userId = this.capacitorService.isNative() ? `mode-mobile-${mymemocUserId}` : `mode-web-${mymemocUserId}`;

        this.pa.setUser( userId, 'user', true);
    }

    public trackSiteSearch(keywords: string): void {
        if (keywords && keywords.length > 0) {
            this.trackPageView('Recherche avancée', `recherche-avancee?q=${keywords}`);
        }
    }

    public async trackArticle(article: Article, eventAction: string, eventLabel?: string, eventValue?: number): Promise<void> {
        if (await this.cannotSendAnalytics()) {
            console.log('[DEACTIVATED][Analytics][trackArticle] ' + JSON.stringify(article));
            return;
        }
        let lignesLibelle: string[] = await this.settingsService.getSubscribedLigneLibelle();
        const rubrique = article.rubrique || 'Aucune rubrique';
        const sousRubrique = article.sousRubrique;
        const typeArticle = article.typeArticle;
        const typeMemo = 'MEMO';
        const formattedTypeArticle = typeArticle === typeMemo ? `FICHE_${typeArticle}` : typeArticle;
        eventLabel = eventLabel === typeMemo ? formattedTypeArticle : eventLabel;
        const infosArticle = `Article_${this.slugifyPipe.transform(article.titre)}_${formattedTypeArticle}_${rubrique}_${sousRubrique}`;
        console.log("article.tags: " + JSON.stringify(article.tags));
        console.log("article.postes" + JSON.stringify(article.postes));
        console.log("article.lignes" + JSON.stringify(article.lignes));
        console.log("article.etablissements" + JSON.stringify(article.etablissements));

        console.log('[Analytics][trackArticle] ' + article.rubrique);
        this.pa.sendEvent('click.action',
            {
                click: eventAction,
                id_article: article.id,
                pagetitle: article.titre,
                template_article: eventLabel,
                rubrique: rubrique,
                sous_rubrique: sousRubrique,
                tags: article.tags?.map(tag => tag.libelle),
                ciblage_poste: article.postes?.map(post => post.libelle),
                lignes: article.lignes?.map(ligne => ligne.libelle),
                ciblage_etablissement: article.etablissements?.map(ment => ment.libelle),
                id_peg_sam: this.user?.id ,
                etablissement: this.user?.etablissementLibelle ,
                poste: this.user?.posteLibelle ,
                secteur: this.user?.secteurLibelle ,
                transilien: this.settingsService.isTransilient(),
                LigneLibelle: lignesLibelle,
            },
        );
        
    }


    public async trackLignes(): Promise<void> {
        const lignesLibelle: string[] = await this.settingsService.getSubscribedLigneLibelle();
        lignesLibelle.map(value => {
            this.trackLigneLibelle(value);
        });
    }


    private trackLigneLibelle(ligneLibelle: string): void {
        this.trackEvent('ligneLibelle', {label: ligneLibelle});
    }

    public trackStatutPeg(enGare: boolean): void {
        this.trackEvent('statut_PEG',{label: String(enGare)});
    }
    public trackContact(contact: string): void {
        this.trackEvent('contact', {label: contact});
    }

    public trackTransilien(isTransilien: boolean): void {
        this.trackEvent('transilien', {label: String(isTransilien)})
        
    }

    public trackPresenceEnGare(eventAction: string, gare: string, listGare: string[]) : void {
        this.trackEvent(eventAction, {gare_de_declaration: gare, liste_gare: listGare});
    }

    public trackLigne(eventAction: string, ligne: string) : void {
        this.trackEvent(eventAction, {label: ligne})
    }

    private trackPropertiesPageDisplay() {
        let lignesLibelle: string[] | null = null;
            (async () => {
                try {
                    lignesLibelle = await this.settingsService.getSubscribedLigneLibelle() || null;
                    console.log("lignesLibelle_trackPropertiesPageDisplay: ", JSON.stringify(lignesLibelle))
                    this.pa.sendEvent('page.display',
                        {
                            page: "StartApp",
                            id_peg_sam: this.user?.id ,
                            etablissement: this.user?.etablissementLibelle ,
                            poste: this.user?.posteLibelle ,
                            secteur: this.user?.secteurLibelle ,
                            transilien: this.settingsService.isTransilient(),
                            isdeclaregare: this.user?.isDeclared ,
                            LigneLibelle: lignesLibelle,
                        },
                    );
                
                } catch (err) {
                  console.error('Failed to load lignesLibelle', err);
                }
              })();
        
    }

}
